import { notification } from "antd";
import instance from "api/cmsapi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { resolveScoreSheetSummaryPayload } from "utils/dpatManualScoreHelpers";
import { emitDpatQueryInvalidation } from "utils/dpatRealtimeQuerySync";

// =========================================================
// API Calls
// =========================================================

// CREATE
export const createComment = async (payload) =>
  await instance.post("comments", payload);

// CREATE BULK
export const createBulkComments = async (payload) =>
  await instance.post("comments/bulk", payload);

// UPDATE
export const updateComment = async ({ id, payload }) =>
  await instance.put(`comments/${id}`, payload);

// GET ALL
export const getAllComments = async () =>
  await instance.get("comments");

// GET BY ID
export const getCommentById = async (id) =>
  await instance.get(`comments/${id}`);

// GET BY TABLE (districtId, year, type)
export const getCommentsByTable = async (districtId, year, type) =>
  await instance.get(`comments/tables/${districtId}/${year}/${type}`);

// GET BY SPECIFIC TABLE (districtId, year, type, tableId)
export const getCommentsBySpecificTable = async (districtId, year, type, tableId) =>
  await instance.get(`comments/table/${districtId}/${year}/${type}/${tableId}`);

/* =========================================================
   React Query Hooks - Comments
   ========================================================= */

// GET ALL
export function useGetAllComments() {
  return useQuery(
    ["getAllComments"],
    getAllComments,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching comments",
        }),
    }
  );
}

// GET BY ID
export function useGetCommentById(id) {
  return useQuery(
    ["getCommentById", id],
    () => getCommentById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching comment",
        }),
    }
  );
}

// GET BY TABLE (districtId, year, type)
const QUERY_STALE_MS = 60_000;

export function useGetCommentsByTable(districtId, year, type, options = {}) {
  return useQuery(
    ["getCommentsByTable", districtId, year, type],
    () => getCommentsByTable(districtId, year, type),
    {
      enabled: (options.enabled ?? true) && !!districtId && !!year && !!type,
      staleTime: QUERY_STALE_MS,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching comments by table",
        }),
    }
  );
}

// GET BY SPECIFIC TABLE (districtId, year, type, tableId)
export function useGetCommentsBySpecificTable(districtId, year, type, tableId, options = {}) {
  return useQuery(
    ["getCommentsBySpecificTable", districtId, year, type, tableId],
    async () => {
      const response = await getCommentsBySpecificTable(districtId, year, type, tableId);
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response)) return response;
      return [];
    },
    {
      enabled: (options.enabled ?? true) && !!districtId && !!year && !!type && !!tableId,
      staleTime: options.staleTime ?? QUERY_STALE_MS,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: options.onError
        ? options.onError
        : (error) =>
            notification.error({
              message:
                error?.response?.data?.message ||
                "Error fetching comments by specific table",
            }),
    }
  );
}

// CREATE
export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation(createComment, {
    onSuccess: () => {
      notification.success({
        message: "Comment created successfully",
      });

      invalidateCommentSummaryQueries(queryClient);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating comment",
      }),
  });
}

// CREATE BULK
export function useCreateBulkComments() {
  const queryClient = useQueryClient();

  return useMutation(createBulkComments, {
    onSuccess: () => {
      notification.success({
        message: "Comments created successfully",
      });

      invalidateCommentSummaryQueries(queryClient);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating comments",
      }),
  });
}

// UPDATE
export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation(updateComment, {
    onSuccess: () => {
      notification.success({
        message: "Comment updated successfully",
      });

      invalidateCommentSummaryQueries(queryClient);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating comment",
      }),
  });
}


function rowsFromResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (payload?.data && typeof payload.data === "object") return [payload.data];
  return [];
}

function commentHasScorePayload(comment) {
  const dddp = comment?.dddpData;
  if (!dddp || typeof dddp !== "object") return false;
  const tableId = String(comment?.tableCommented || dddp.indicator || "").toLowerCase();
  if (tableId === "assessment_start_dapt" && dddp.tables && typeof dddp.tables === "object") {
    return true;
  }
  const scores = dddp.manualScores || dddp.scores || dddp;
  return Boolean(
    String(scores.technicalTeamScore || dddp.technicalTeamScore || "").trim() ||
      String(scores.qualityAssuranceScore || dddp.qualityAssuranceScore || "").trim() ||
      String(scores.petitionCommitteeScore || dddp.petitionCommitteeScore || "").trim(),
  );
}

// GET SCORESHEET SUMMARY (includes manual-score cascade for final totals)
export const getScoreSheetSummary = async (districtId, year, type, districtName, signal, queryClient) => {
  const config = signal ? { signal } : undefined;
  const ignoreMissing = (error) => {
    if (signal?.aborted || error?.code === "ERR_CANCELED") throw error;
    return { data: [] };
  };
  const commentsKey = ["getCommentsByTable", districtId, year, type];
  const startKey = ["getCommentsBySpecificTable", districtId, year, type, "assessment_start_DAPT"];
  const fetchComments = () =>
    instance.get(`comments/tables/${districtId}/${year}/${type}`, config).catch(ignoreMissing);
  const fetchStart = async () => {
    const response = await instance
      .get(`comments/table/${districtId}/${year}/${type}/assessment_start_DAPT`, config)
      .catch(ignoreMissing);
    const rows = rowsFromResponse(response?.data ?? response);
    return rows;
  };
  const [summaryRes, commentsRes, startRows] = await Promise.all([
    instance.get(`comments/summary/${districtId}/${year}/${type}`, config),
    queryClient
      ? queryClient.fetchQuery(commentsKey, fetchComments, { staleTime: QUERY_STALE_MS })
      : fetchComments(),
    queryClient
      ? queryClient.fetchQuery(startKey, fetchStart, { staleTime: QUERY_STALE_MS })
      : fetchStart(),
  ]);

  const comments = rowsFromResponse(commentsRes?.data ?? commentsRes).filter(commentHasScorePayload);
  const bulkHasStartTables = comments.some((comment) => {
    const tables = comment?.dddpData?.tables;
    return (
      String(comment?.tableCommented || comment?.dddpData?.indicator || "").toLowerCase() ===
        "assessment_start_dapt" &&
      tables &&
      typeof tables === "object" &&
      Object.keys(tables).length > 0
    );
  });
  const mergedComments = bulkHasStartTables
    ? comments
    : [...comments, ...(Array.isArray(startRows) ? startRows : rowsFromResponse(startRows))];

  const resolved = resolveScoreSheetSummaryPayload(summaryRes?.data, mergedComments, {
    districtId,
    districtName,
  });
  return { data: resolved };
};

// React Query Hook for Summary
export function useGetScoreSheetSummary(districtId, year, type = "DPAT", districtName) {
  const queryClient = useQueryClient();
  return useQuery(
    ["getScoreSheetSummary", districtId, year, type],
    ({ signal }) => getScoreSheetSummary(districtId, year, type, districtName, signal, queryClient),
    {
      enabled: !!districtId && !!year && !!type,
      staleTime: QUERY_STALE_MS,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching score sheet summary",
        }),
    }
  );
}

// GET COMMENTS AND GAPS SUMMARY
export const getCommentsAndGapsSummary = async (districtId, year, type) =>
  await instance.get(`comments/comments-gaps-summary/${districtId}/${year}/${type}`);

export function invalidateCommentSummaryQueries(queryClient, options = {}) {
  queryClient.invalidateQueries(["getCommentsAndGapsSummary"]);
  queryClient.invalidateQueries(["getScoreSheetSummary"]);
  queryClient.invalidateQueries(["getCommentsByTable"]);
  queryClient.invalidateQueries(["getCommentsBySpecificTable"]);
  if (options.broadcast !== false) {
    emitDpatQueryInvalidation(options.payload);
  }
}

// React Query Hook for Summary
export function useGetCommentsAndGapsSummary(districtId, year, type = "DPAT", options = {}) {
  return useQuery(
    ["getCommentsAndGapsSummary", districtId, year, type],
    () => getCommentsAndGapsSummary(districtId, year, type),
    {
      enabled: (options.enabled ?? true) && !!districtId && !!year && !!type,
      staleTime: QUERY_STALE_MS,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching comments and gaps summary",
        }),
    }
  );
}